import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { debounce } from "lodash";

export type DebounceOptions = NonNullable<Parameters<typeof debounce>[2]>;

export type UseBottomScrollListenerOptions = {
  offset?: number;
  debounce?: number;
  debounceOptions?: DebounceOptions;
  triggerOnNoScroll?: boolean;
};

export type BottomScrollListenerRef<T extends HTMLElement> = ((
  node: T | null
) => void) & {
  current: T | null;
};

type ScrollMetrics = {
  scrollTop: number;
  visibleHeight: number;
  scrollHeight: number;
};

type ScrollTarget = HTMLElement | Window;

type BottomHandler = () => void;

const DEFAULT_DEBOUNCE_MS = 200;
const DEFAULT_DEBOUNCE_OPTIONS: DebounceOptions = {
  // Fire event immediately on scroll
  leading: true,
  // Don't fire event after cooldown period
  trailing: false,
};

function getDebounceOptions({
  hasCustomOptions,
  leading,
  maxWait,
  trailing,
}: {
  hasCustomOptions: boolean;
  leading: DebounceOptions["leading"];
  maxWait: DebounceOptions["maxWait"];
  trailing: DebounceOptions["trailing"];
}): DebounceOptions {
  if (!hasCustomOptions) {
    return DEFAULT_DEBOUNCE_OPTIONS;
  }

  const nextOptions: DebounceOptions = { ...DEFAULT_DEBOUNCE_OPTIONS };

  if (leading !== undefined) {
    nextOptions.leading = leading;
  }
  if (maxWait !== undefined) {
    nextOptions.maxWait = maxWait;
  }
  if (trailing !== undefined) {
    nextOptions.trailing = trailing;
  }

  return nextOptions;
}

function getScrollMetrics(element: HTMLElement | null): ScrollMetrics {
  if (element !== null) {
    return {
      scrollTop: element.scrollTop,
      visibleHeight: element.clientHeight,
      scrollHeight: element.scrollHeight,
    };
  }

  const scrollingElement =
    document.scrollingElement ?? document.documentElement;

  return {
    scrollTop: scrollingElement.scrollTop,
    visibleHeight: window.innerHeight,
    scrollHeight: scrollingElement.scrollHeight,
  };
}

function isAtScrollBottom(metrics: ScrollMetrics, offset: number): boolean {
  return (
    Math.ceil(metrics.scrollTop + metrics.visibleHeight) >=
    metrics.scrollHeight - offset
  );
}

function addScrollListener(target: ScrollTarget, handler: BottomHandler) {
  target.addEventListener("scroll", handler, { passive: true });
}

function removeScrollListener(target: ScrollTarget, handler: BottomHandler) {
  target.removeEventListener("scroll", handler);
}

/**
 * Invokes a callback when the user scrolls to the bottom of an element or the viewport.
 * Pass the returned ref to a scrollable element to listen on that element; otherwise listens on window.
 */
export function useBottomScrollListener<T extends HTMLElement = HTMLDivElement>(
  onBottom: BottomHandler,
  options?: UseBottomScrollListenerOptions
): BottomScrollListenerRef<T> {
  const offset = options?.offset ?? 0;
  const debounceMs = options?.debounce ?? DEFAULT_DEBOUNCE_MS;
  const triggerOnNoScroll = options?.triggerOnNoScroll ?? false;
  const hasCustomDebounceOptions = options?.debounceOptions !== undefined;
  const debounceLeading = options?.debounceOptions?.leading;
  const debounceMaxWait = options?.debounceOptions?.maxWait;
  const debounceTrailing = options?.debounceOptions?.trailing;

  // Create stable debounce options
  const debounceOptions = useMemo(
    () =>
      getDebounceOptions({
        hasCustomOptions: hasCustomDebounceOptions,
        leading: debounceLeading,
        maxWait: debounceMaxWait,
        trailing: debounceTrailing,
      }),
    [
      hasCustomDebounceOptions,
      debounceLeading,
      debounceMaxWait,
      debounceTrailing,
    ]
  );

  const onBottomRef = useRef(onBottom);
  onBottomRef.current = onBottom;

  const elementRef = useRef<T | null>(null);
  // Used to trigger rerun effects when the scroll element changes.
  // elementRef.current remains the authoritative listener target.
  const [scrollElement, setScrollElement] = useState<T | null>(null);

  // Scroll ref keeps scrollElement synchronized with the scrollable element
  const scrollRef = useMemo<BottomScrollListenerRef<T>>(() => {
    const ref = ((node: T | null) => {
      ref.current = node;
      elementRef.current = node;
      setScrollElement(node);
    }) as BottomScrollListenerRef<T>;
    ref.current = null;

    return ref;
  }, []);

  const debouncedOnBottom = useMemo(
    () => debounce(() => onBottomRef.current(), debounceMs, debounceOptions),
    [debounceMs, debounceOptions]
  );

  const handleScroll = useCallback(() => {
    const metrics = getScrollMetrics(elementRef.current);

    if (isAtScrollBottom(metrics, offset)) {
      debouncedOnBottom();
    }
  }, [offset, debouncedOnBottom]);

  useEffect(() => {
    // scrollElement triggers this effect after ref changes;
    const target = elementRef.current ?? window;
    addScrollListener(target, handleScroll);

    if (triggerOnNoScroll) {
      handleScroll();
    }

    return () => {
      removeScrollListener(target, handleScroll);
    };
  }, [handleScroll, scrollElement, triggerOnNoScroll]);

  useEffect(() => {
    return () => {
      debouncedOnBottom.cancel();
    };
  }, [debouncedOnBottom]);

  return scrollRef;
}

export default useBottomScrollListener;
